/* eslint-disable @nx/enforce-module-boundaries */
import moment from 'moment';
import { lastValueFrom } from 'rxjs';

/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
} from '@angular/forms';
import { FileApiClient } from '@ellf/app-api-client';
import { NgrxLoaderFacade } from '@ellf/app-ngrx-loader';
import { AttachmentDto } from '@ellf/common-dto';
import { isImage, isPdf, sanitizeFileName } from '@ellf/common-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

let nextId = 0;

@Component({
  selector: 'ellf-file-reader',
  templateUrl: './file-reader.component.html',
  styleUrls: ['./file-reader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileReaderComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileReaderComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class FileReaderComponent implements OnDestroy, ControlValueAccessor {
  id = `fr-${nextId++}`;

  @ViewChild('input', { static: true })
  input?: ElementRef;

  @Input()
  accept = '*';

  @Input()
  maximumSizeMB = 10;

  @Input()
  multiple = false;

  @Input()
  canUploadFiles = true;

  @Input()
  orderDesc = false;

  @Input()
  readOnly = false;

  @Output()
  fileChange = new EventEmitter<FileList>();

  @Output()
  maxSizeError = new EventEmitter<number>();

  value?: AttachmentDto[] = [];
  isValid = true;

  isLoading = false;

  selectedAttachment?: AttachmentDto;

  isDisabled = false;

  // get filename() {
  //   if (!this.value) {
  //     return '';
  //   }
  //   return this.value.name.substring(this.value.name.lastIndexOf('/') + 1);
  // }

  constructor(
    private cd: ChangeDetectorRef,
    private uploadService: FileApiClient,
    private dialogService: NgbModal,
    private loaderFacade: NgrxLoaderFacade,
  ) {}

  propagateChange = (file?: AttachmentDto[]) => {};
  onTouched = () => {};
  validateFn: any = () => {};

  writeValue(value?: AttachmentDto[]) {
    this.value = Array.isArray(value) ? [...value] : undefined;
    this.cd.detectChanges();
  }

  registerOnChange(fn: (value?: AttachmentDto[]) => void) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean) {
    this.isDisabled = isDisabled;
    this.cd.markForCheck();
  }

  validate(control: UntypedFormControl) {
    const res = this.validateFn(control);
    this.isValid = res == null;
    return res;
  }

  ngOnDestroy() {
    this.cd.detach();
  }

  async onSelectFile(event: any) {
    if (event.target.files && event.target.files.length) {
      const files = event.target.files as FileList;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > this.maximumSizeMB * 1024 * 1024) {
          this.maxSizeError.emit(this.maximumSizeMB);
          alert(
            `La dimensione non può essere superiore a ${this.maximumSizeMB} MB.`,
          );
          return;
        }
      }

      if (this.fileChange) {
        this.fileChange.emit(files);
      }
      if (this.canUploadFiles) {
        await this.onLoad(files);
      } else {
        if (!this.value) {
          this.value = [];
        }
        this.value = [
          ...this.value,
          ...Array.from(files).map((f) => ({
            filename: f.name,
            title: f.name,
            mimeType: '',
            url: '',
            // createdDate: moment().format('DD/MM/YYYY HH:mm'),
          })),
        ];
      }
    }
  }

  getImagePreviewUrl(media: AttachmentDto) {
    return media.url;
  }

  isImage(media: AttachmentDto) {
    return isImage(media.mimeType);
  }

  isPdf(media: AttachmentDto) {
    return isPdf(media.mimeType);
  }
  private async onLoad(files?: FileList) {
    if (files && files.length) {
      this.isLoading = true;
      try {
        const res = await lastValueFrom(this.uploadService.upload(files));

        if (!this.value) {
          this.value = [];
        }
        // ordina con parametro orderDesc
        if (this.orderDesc) {
          this.value = [...res, ...this.value];
        } else {
          this.value = [...this.value, ...res];
        }

        this.propagateChange(this.value);
        this.onTouched();

        if (this.input) {
          this.input.nativeElement.value = null;
        }

        this.isLoading = false;
        this.cd.detectChanges();
      } catch (err) {
        this.isLoading = false;
        this.cd.detectChanges();
      }
    }
  }

  public remove(file: AttachmentDto) {
    if (confirm("Sei sicuro di voler eliminare l'elemento?")) {
      // this.uploadService.delete(file.id).subscribe((res) => {
      this.value = this.value?.filter((f) => f !== file);
      this.propagateChange(this.value);
      this.onTouched();
      this.cd.detectChanges();
      //console.log('attachment removed');
      // });
      if (this.fileChange) {
        this.fileChange.emit(undefined);
      }
    }
  }

  public async download(file: AttachmentDto, newTab = false) {
    if (!file.filename) {
      return;
    }
    this.loaderFacade.showLoader({ type: 'file-reader-download' });
    const blob = await lastValueFrom(this.uploadService.download(file));
    this.downloadFile(blob, file.mimeType, file.filename, newTab);
  }

  private downloadFromUrl(url: string, name: string) {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', url);
    link.setAttribute('download', sanitizeFileName(name));
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  private downloadFile(
    data: ArrayBuffer,
    type: string,
    name: string,
    newTab: boolean,
  ) {
    const blob = new Blob([data], { type: type });
    const url = window.URL.createObjectURL(blob);
    if (newTab) {
      window.open(url, '_blank');
    } else {
      this.downloadFromUrl(url, name);
      window.URL.revokeObjectURL(url);
    }
    this.loaderFacade.hideLoader({ type: 'file-reader-download' });
  }

  public zoom(file: AttachmentDto, content: any) {
    this.selectedAttachment = file;
    this.dialogService.open(content, {
      size: 'xl',
    });
  }

  formatDate(createdDate: string | null | undefined) {
    if (createdDate) {
      const mom = moment(createdDate);
      if (mom.isValid()) return mom.format('DD/MM/YYYY HH:mm');
      return createdDate;
    }
    return '';
  }
}
